import React from "react";
import { Paper, Typography, Stack, alpha, useTheme, Box } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import { formatAmount } from "../../utils/hooks/dataHandler";
import DetailsContainer from "./utils/DetailsContainer";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { TextFieldForAmount } from "../utils/TextField/NumericTextField";
import { JustInfo } from "../utils/Icon";
import { TextFieldForViewOnly } from "../utils/TextField/TextField";
import YesNoSwitch from "../utils/button/YesNoSwitch";

const CorporateRebateField = ({
  label,
  value,
  onChange,
  disabled,
  sx,
  info,
  canEdit = true,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.2),
        bgcolor: alpha(theme.palette.background.paper, 0.8),
        flex: 1,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{
          fontWeight: 500,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          mb: 2,
        }}
      >
        {label} {info && <JustInfo title={info} />}
      </Typography>
      {canEdit ? (
        <TextFieldForAmount
          value={value || ""}
          onChange={(newValue) => onChange(newValue)}
          disabled={disabled}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: alpha(theme.palette.divider, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
      ) : (
        <TextFieldForViewOnly value={formatAmount(value) || ""} />
      )}
    </Paper>
  );
};

const CorporateRebate = ({
  id,
  dataId,
  data,
  totalAmount,
  onUpdate,
  disabled = false,
  loading,
  currentYearRebate,
  company,
  canEdit = true,
  isInvestmentCompany = false,
  isCrossYA = false,
}) => {
  const theme = useTheme();
  const currentYA = Number(company?.yearOfAssessment);
  const precedingYA = currentYA - 1;

  const handleRebateChange = (field, value, yaKey) => {
    if (onUpdate) {
      onUpdate(field, value, yaKey);
    }
  };

  const getYAData = (yaKey) => data?.[yaKey] || {};
  const getCashGrant = (yaKey) =>
    isCrossYA ? currentYearRebate?.[yaKey] || {} : currentYearRebate || {};

  const renderCashGrantToggle = (yaKey) => {
    const expectedYA = isCrossYA
      ? yaKey === "preceding"
        ? precedingYA
        : currentYA
      : currentYA;
    const cashGrant = getCashGrant(yaKey);
    const showCashGrant =
      !isInvestmentCompany &&
      cashGrant.year &&
      cashGrant.amount > 0 &&
      cashGrant.year === expectedYA;

    if (!showCashGrant) {
      if (!isCrossYA || isInvestmentCompany) return null;

      return (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.grey[500], 0.04),
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.18),
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            No CIT Rebate Cash Grant is available for YA {expectedYA}.
          </Typography>
        </Box>
      );
    }

    const value = isCrossYA ? getYAData(yaKey)?.isRebate : data?.isRebate;

    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: "text.primary",
            flex: 1,
          }}
        >
          Company has received the ${formatAmount(cashGrant.amount)} CIT Rebate
          Cash Grant ({cashGrant.year} only)
        </Typography>
        {canEdit ? (
          <YesNoSwitch
            value={value}
            onChange={(nextValue) =>
              handleRebateChange("isRebate", nextValue, yaKey)
            }
            disabled={disabled}
          />
        ) : (
          <TextFieldForViewOnly value={value ? "Yes" : "No"} />
        )}
      </Stack>
    );
  };

  const renderFields = (yaKey = null) => {
    const source = yaKey ? getYAData(yaKey) : data;

    return (
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <CorporateRebateField
            label="CIT Rebate (%)"
            value={source?.taxableRate || ""}
            onChange={(newValue) =>
              handleRebateChange("taxableRate", newValue, yaKey)
            }
            disabled={disabled}
            canEdit={canEdit}
          />
          <CorporateRebateField
            label="Capped Amount"
            value={source?.cappedAmount || ""}
            onChange={(newValue) =>
              handleRebateChange("cappedAmount", newValue, yaKey)
            }
            disabled={disabled}
            canEdit={canEdit}
          />
        </Stack>
        {renderCashGrantToggle(yaKey)}
      </Stack>
    );
  };

  const renderYACard = (yaKey, label) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.18),
        bgcolor: "background.paper",
        flex: 1,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          fontWeight: 700,
          color: "text.primary",
        }}
      >
        {label}
      </Typography>
      {renderFields(yaKey)}
    </Paper>
  );

  return (
    <DetailsContainer
      id={id}
      title="Corporate Income Tax Rebate"
      totalAmount={totalAmount}
      icon={CorporateFareIcon}
      loading={loading}
      showDecimal
    >
      <Box sx={{ p: 3 }}>
        {isCrossYA ? (
          <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
            {renderYACard("preceding", `Preceding YA ${precedingYA}`)}
            {renderYACard("current", `Current YA ${currentYA}`)}
          </Stack>
        ) : (
          renderFields()
        )}
      </Box>
    </DetailsContainer>
  );
};

export default CorporateRebate;
